import {
  ActionType,
  ActionWidget,
  ActiveRecord,
  buildSingleItemParam,
  getEasyImportTemplate,
  PushActiveRecordsPredict,
  PopupSubmitParameters,
  FormComplexListFieldWidget,
  isRelation2MField,
  ModelFieldType,
  RuntimeM2MField,
  RuntimeModelField,
  RuntimeO2MField,
  SPI,
  SubmitCacheManager,
  useMatched,
  ActiveRecordsOperator,
  ValidatorStatus,
  ViewActionTarget,
  Widget
} from '@kunlun/dependencies';
import { OioMessage, Optional, WidgetTrigger } from '@kunlun/vue-ui-antd';
import XLSX from 'xlsx';
import ImportCom from './FormTableImportV2ActionWidget.vue';
import { ErrorCode, ValidateExcelError } from './errors';
import { ExcelModelField } from './types';
import { cloneDeep } from 'lodash-es';
import { queryWorkbook, validateData } from '../../../api';

// 辅助函数：生成唯一键
function createUniqueKey(pks: string[], item: ActiveRecord) {
  return pks.map((key) => item[key]).join('|'); // 使用分隔符连接各个属性值
}

@SPI.ClassFactory(
  ActionWidget.Token({
    widget: 'FormTableImportV2',
    actionType: [ActionType.View],
    target: [ViewActionTarget.Dialog, ViewActionTarget.Router, ViewActionTarget.Inner]
  })
)
export class formTableImportV2 extends ActionWidget {
  public initialize(config) {
    super.initialize(config);
    this.setComponent(ImportCom);
    this.copyModleFields = cloneDeep(this.model.modelFields);

    return this;
  }

  @Widget.Reactive()
  protected get label() {
    return this.getDsl().label || this.action?.displayName || '导入';
  }

  @Widget.Reactive()
  protected validation1: { status: ValidatorStatus; help: string } = {
    status: ValidatorStatus.Success,
    help: ''
  };

  @Widget.Reactive()
  protected validation2: { status: ValidatorStatus; help: string } = {
    status: ValidatorStatus.Success,
    help: ''
  };

  protected firstOpen = true;

  @Widget.Reactive()
  protected visible = false;

  @Widget.Reactive()
  protected importLoading = false;

  @Widget.Reactive()
  protected selectValue = '';

  @Widget.Reactive()
  protected workbookList: any[] = [];

  @Widget.Reactive()
  protected errorArr: any[] = [];

  @Widget.Reactive()
  protected errorTableColums: any[] = [];

  protected headerFields: ExcelModelField[] = [];

  protected copyModleFields: RuntimeModelField[] = [];

  @Widget.Reactive()
  get importErrorMsg() {
    return '导入成功' + (this.errorArr.length ? '，部分数据错误' : '');
  }

  protected headerFieldsArrMap = {
    enFieldsArr: [] as any[],
    enFieldsKeyArr: [] as any[],
    cnFieldsArr: [] as any[]
  };

  public enableUniqueDatasource = true;

  @Widget.Reactive()
  protected handleSelectChange(val) {
    this.selectValue = val;
  }

  public clickAction() {
    this.openDialog(true);
  }

  @Widget.Method()
  protected async downloadTemplate() {
    if (!this.selectValue) {
      this.validation1.status = ValidatorStatus.Error;
      this.validation1.help = this.translate('kunlun.import.tips');
      return;
    }
    const urlObj: any = await getEasyImportTemplate(this.selectValue);
    window.open(urlObj.redirectUri);
  }

  @Widget.Method()
  protected async openDialog(val) {
    const importTemplateName = this.getConfig('importTemplateName');
    this.validation1 = {
      status: ValidatorStatus.Success,
      help: ''
    };
    this.validation2 = { status: ValidatorStatus.Success, help: '' };
    this.selectValue = '';
    if (this.firstOpen && val) {
      this.firstOpen = false;
      this.workbookList = (await queryWorkbook(this.model, false, importTemplateName as string)).content;
    }
    if (this.workbookList && this.workbookList.length > 0) {
      this.selectValue = this.workbookList[0].id;
    }

    this.generateHeader();
    this.visible = val;
  }

  /**
   * 生成表头字段
   *
   * @protected
   * @memberof formTableImportV2
   */
  protected generateHeader() {
    const sheetDefinitions = JSON.parse(this.workbookList[0].sheetDefinitions);
    const enFieldsArr = sheetDefinitions[0].blockDefinitionList[0].headerList[0].cellList;
    const enFieldsKeyArr = enFieldsArr.map((enField) => enField.field);
    const cnFieldsArr = sheetDefinitions[0].blockDefinitionList[0].headerList[1].cellList;
    this.headerFieldsArrMap = {
      enFieldsArr,
      enFieldsKeyArr,
      cnFieldsArr
    };
    const headerFields: any[] = [];
    enFieldsKeyArr.forEach((i, index) => {
      this.copyModleFields.forEach((j) => {
        if (j.name === i) {
          // @ts-ignore
          j.excelLabel = cnFieldsArr[index].value;
          headerFields.push(j);
        }
      });
    });

    this.headerFields = headerFields;
  }

  @Widget.Method()
  public afterClick(e) {}

  /**
   * 读取文件内容
   *
   * @protected
   * @param {*} e
   * @memberof formTableImportV2
   */
  @Widget.Method()
  protected async readFile(e) {
    try {
      this.importLoading = true;
      const file = <File>e.target.files[0];
      this.afterClick(file);
      const format = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
      const importFileFormat = ['xlsx', 'xls'];
      if (!importFileFormat.includes(format)) {
        OioMessage.error('仅支持上传excel文件');
      } else {
        const reader = new FileReader();
        reader.onload = async (e: any) => {
          const workbook = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
          const sheetNames = workbook.SheetNames;
          const worksheet = workbook.Sheets[sheetNames[0]];
          const sheetList = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false }) as any[];

          try {
            const res = this.outputWorkbookByFields(sheetList, this.headerFields);
            const addData = await this.validateExcelDataAfterOutput(res);
            // @ts-ignore
            this.fillDatasource(addData);
            OioMessage.info(this.importErrorMsg);
          } catch (error) {
            if (error instanceof ValidateExcelError) {
              OioMessage.error(error.message);
            } else {
              console.error(error);
            }
          } finally {
            this.importLoading = false;
          }
        };
        reader.readAsBinaryString(file);
      }
      // 需要清除input的value，不然相同的文件无法上传多次
      if (e.target.value) {
        e.target.value = '';
      }
    } catch (error) {
      console.error(error);
      this.importLoading = false;
    }
  }

  /**
   * 根据字段输出表格数据
   *
   * @protected
   * @param {XLSX.WorkBook} workbook
   * @memberof formTableImportV2
   */
  protected outputWorkbookByFields(sheetList: any[], filterFields: ExcelModelField[]) {
    const maxLength = this.getConfig('maxLength') || 2000;
    const importResult: any[] = [];

    if (!sheetList.length) {
      throw new ValidateExcelError(ErrorCode.OTHER, '表格数据为空');
    } else if (sheetList.length > Number(maxLength)) {
      throw new ValidateExcelError(ErrorCode.OTHER, `当前导入仅支持导入${maxLength}行明细数据导入，请核对数据后重新导入`);
    }

    // 行数据
    rowfor: for (let i = 0; i < sheetList.length; i++) {
      const row = sheetList[i];
      const formattedRow: Record<string, any> = {};

      // 表头对应的字段
      for (let j = 0; j < filterFields.length; j++) {
        const field = filterFields[j];
        const cellData = this.formatCellData(row[field.excelLabel!], field);
        if (typeof cellData === 'string') {
          if (cellData.trim()) {
            formattedRow[field.name] = cellData.trim();
          }
        } else {
          formattedRow[field.name] = cellData;
        }
        formattedRow.rowNum = i + 1; // 行号
      }

      importResult.push(formattedRow);
    }

    return importResult;
  }

  /**
   * 纠正单元格数据的类型
   *
   * @protected
   * @param {*} cellData
   * @param {*} field
   * @return {*}
   * @memberof formTableImportV2
   */
  protected formatCellData(cellData, field) {
    const cellDataType = typeof cellData;

    if (!cellData) {
      return cellData;
    }

    // 字符串类型
    if (field.ttype == ModelFieldType.String && cellDataType !== 'string') {
      return String(cellData);
    }
    // 日期类型
    else if (field.ttype == ModelFieldType.DateTime && cellDataType !== 'string') {
      let date = new Date(cellData);
      date = new Date(date.getTime() + 43 * 1000);
      const yy = date.getFullYear();
      const mm = date.getMonth() + 1;
      const dd = date.getDate();
      const hh = date.getHours();
      const m = date.getMinutes();
      const ss = date.getSeconds();

      return `${yy}-${mm}-${dd} ${hh}:${m}:${ss}`;
    }
    // 去除小数后面的0
    else if (field.ttype == ModelFieldType.Float) {
      return Number(cellData);
    }
    // 枚举类型填充正确的值
    else if (field.ttype == ModelFieldType.Enum) {
      const index = this.headerFieldsArrMap.enFieldsKeyArr.indexOf(field.name as never);
      if (index === -1) {
        throw new Error('未找到枚举字段' + field.name);
      }
      const enumMapStr = this.headerFieldsArrMap.enFieldsArr[index].format;
      if (!enumMapStr) {
        throw new Error('枚举字段' + field.name + '未配置format');
      }
      const enumMap = JSON.parse(enumMapStr);
      for (const key in enumMap) {
        if (enumMap[key] === cellData) {
          return key;
        }
      }
    }

    return cellData;
  }

  protected getUniqueKeyFields(fieldWidget: FormComplexListFieldWidget): string[] {
    const { uniqueKeyFields } = this.getAllConfig();
    let uniqueKeyFieldList = [] as string[];
    if (!uniqueKeyFields) {
      uniqueKeyFieldList = fieldWidget.model.pks!;
    } else {
      try {
        const list = JSON.parse(uniqueKeyFields as string);
        if (list.length) {
          uniqueKeyFieldList = list;
        } else {
          uniqueKeyFieldList = fieldWidget.model.pks!;
        }
      } catch (e) {
        console.log('getUniqueKeyFields parse error', e);
      }
    }

    return uniqueKeyFieldList.length ? uniqueKeyFieldList : ['id'];
  }

  protected getImportRootData() {
    return this.rootData![0];
  }

  /**
   * 校验表格数据(后端校验)
   *
   * @protected
   * @param {*} resultList
   * @memberof formTableImportV2
   */
  protected async validateExcelDataAfterOutput(resultList: any[]) {
    let { rowNumFiled, validateMsgFiled, validateFunction, enableEnumStr, agsWithFormData } = this.getAllConfig();
    if (!rowNumFiled || !validateMsgFiled) {
      console.error('rowNumFiled 或 validateMsgFiled 未配置');
      rowNumFiled = 'rowNum';
      validateMsgFiled = 'validateMsg';
    }
    if (!validateFunction) {
      throw new ValidateExcelError(ErrorCode.OTHER, 'validateFunction 未配置');
    }

    const configFields = [rowNumFiled, validateMsgFiled].map((i, index) => ({
      label: index === 0 ? '行号' : '错误信息',
      name: i,
      ttype: ModelFieldType.String
    }));
    this.errorTableColums = [...this.headerFields, ...configFields];

    // FIXME:这里已经在formatCellData中处理了，所以这里不需要再处理，找个时间干掉enableEnumStr这个配置测一下看
    // 枚举加字段，类型为String，[keyStr]: "enum"
    if (enableEnumStr) {
      this.copyModleFields
        .filter((i) => i.ttype === ModelFieldType.Enum)
        .forEach((i) => {
          const originName = i.name;
          i.data += 'Str';
          i.name += 'Str';
          i.ttype = ModelFieldType.String;
          // 标记返回类型为enum
          // @ts-ignore
          i.returnType = ModelFieldType.Enum;

          resultList.forEach((j) => {
            j[i.name] = j[originName];
          });
        });
    }

    const promiseArr: Promise<any>[] = resultList.map(
      // @ts-ignore
      async (i) => await buildSingleItemParam(this.errorTableColums, i)
    );
    const importData = await Promise.all(promiseArr);
    const modelFields = [...this.copyModleFields.filter((i) => i.name !== 'id'), ...configFields];
    try {
      const results = await validateData(
        { ...this.model, modelFields },
        validateFunction,
        importData,
        agsWithFormData ? JSON.stringify(JSON.stringify(this.getImportRootData())) : ''
      );
      // @ts-ignore
      this.errorArr = results.filter((i) => i[validateMsgFiled]) || [];
      // @ts-ignore
      return results.filter((i) => !i[validateMsgFiled]) || [];
    } catch (error) {
      OioMessage.error('excel数据格式错误，请检查excel数据。');
      throw error;
    }
  }

  /**
   * 填充表格数据前的处理，可以在这里对数据进行处理，比如计算其他列的值，或者填充新的字段
   *
   * @param {ActiveRecord[]} addDataList
   * @return {*}
   * @memberof formTableImportV2
   */
  public changeDataBeforeFillDatasource(addDataList: ActiveRecord[], subviewFieldWidget: FormComplexListFieldWidget, isCoverImport: boolean): { merged: any[]; duplicates: any[] } {
    if (isCoverImport) {
      //对所有数据进行去重
      return { merged: addDataList, duplicates: [] };
    } else {
      const result = this.mergeArraysUniqueByKeys(addDataList, subviewFieldWidget.dataSource ?? [], this.getUniqueKeyFields(subviewFieldWidget));
      return result;
    }
  }

  public arrayDeduplication(data, subviewFieldWidget) {}

  // 去重合并函数，基于多个属性
  public mergeArraysUniqueByKeys(addDataList: ActiveRecord[], dataSource: ActiveRecord[], pks: string[]) {
    let { validateMsgFiled } = this.getAllConfig();
    validateMsgFiled = validateMsgFiled || 'validateMsg';
    const map = new Map();
    const duplicates: any[] = [];

    // 遍历第一个数组，填充Map
    addDataList.forEach((item) => map.set(createUniqueKey(pks, item), item));

    // 遍历第二个数组，更新或添加到Map，并记录重复项
    dataSource.forEach((item) => {
      const key = createUniqueKey(pks, item);
      if (map.has(key)) {
        // 如果需要合并而不是覆盖，可以根据需求修改这里的逻辑
        // 这里简单地用dataSource的值覆盖addDataList中已有的值
        let errorObj = { ...item };
        errorObj[validateMsgFiled as string] = '数据重复';
        duplicates.push(errorObj); // 记录重复项
        map.set(key, item);
      } else {
        map.set(key, item);
      }
    });

    // 将Map转换回数组
    const mergedArray = Array.from(map.values());

    return {
      merged: mergedArray,
      duplicates: duplicates
    };
  }

  public uniqueDatasource(data, subviewFieldWidget: FormComplexListFieldWidget) {
    const uniqData = ({ fieldWidget, includeModels, includeViewName }) => {
      let results: any[] = [];
      const viewName = fieldWidget.view!.name!;
      const model = fieldWidget.model.model;
      const dataMap = {};
      const uniqueKeyFields = this.getUniqueKeyFields(fieldWidget); // 支持多主键

      // data去重
      data.forEach((item) => {
        dataMap[createUniqueKey(uniqueKeyFields, item)] = item;
      });
      data = Object.values(dataMap);

      // data与表格数据去重
      if (includeModels.includes(model) && includeViewName.includes(viewName)) {
        const keys = fieldWidget.dataSource!.map((item) => createUniqueKey(uniqueKeyFields, item));
        data.forEach((item) => {
          if (!keys.includes(item.tradeDetailId)) {
            results.push(item);
          }
        });
      } else {
        results = data;
      }
      return results;
    };

    // 要货计划表格去重
    const results = uniqData({
      fieldWidget: subviewFieldWidget,
      includeModels: [
        // 'deli.aries.seller.DeliAriesSellerPurchasingPlan',
        // 'deli.aries.seller.SellerDistributorTradeOrder',
        // 'deli.aries.merchant.DeliAriesBuyerPurchasingPlan'
      ],
      includeViewName: [
        // '要货计划表单_FORM_uiVieweb96ec729ee645f4814835199ff94c0d',
        // '经销要货计划表单_FORM_uiView5e21ad9d0bd746ac99ff7265ea450780'
      ]
    });
    return results;
  }

  /**
   * 填充表格数据
   *
   * @protected
   * @param {any[]} data
   * @memberof formTableImportV2
   */
  protected fillDatasource(data: any[]) {
    try {
      const { isCoverImport } = this.getAllConfig();
      const field = Optional.ofNullable(this.metadataRuntimeContext.field)
        .filter<RuntimeO2MField | RuntimeM2MField | undefined>((v) => isRelation2MField(v!))
        .orElse(undefined);
      const subviewFieldWidget = Optional.ofNullable(this.metadataRuntimeContext.handle)
        .map(Widget.select)
        .map<FormComplexListFieldWidget | undefined>((v) => v!.getParent() as unknown as FormComplexListFieldWidget)
        .orElse(undefined);

      if (!field || !subviewFieldWidget) {
        console.error('Invalid field and subview widget');
        return;
      }

      const showRecords = subviewFieldWidget.dataSource;
      const { submitCache } = field;
      const subviewSubmitCache = this.metadataRuntimeContext.extendData.subviewSubmitCache as SubmitCacheManager;

      let results = data;
      if (this.enableUniqueDatasource) {
        results = this.uniqueDatasource(data, subviewFieldWidget);
      }
      if (showRecords) {
        const deleteActionNames = ['uiView738044eb227a44339a32702e38db3951'];
        const { merged: importData, duplicates } = this.changeDataBeforeFillDatasource(
          results,
          subviewFieldWidget,
          isCoverImport ? true : deleteActionNames.includes(this.action?.name)
        );
        const ttype = this.rootViewRuntimeContext.fields[0].ttype;
        const cacheIncludeModels = ['deli.promotion.PromotionPartnerRelation'];
        const flushDataSourceModels = ['deli.promotion.PromotionPartnerRelation'];
        const actionNames = [
          'uiViewc106fc16cb884bfda5cd6bdc318ae9e9', // deli.promotion.PromotionPartnerRelation 添加客户表单-活动客户导入
          'uiViewa59c822a2c1a4442816940ba60742e57', // deli.aries.item.DeliItemFloorChildItem 楼层表单
          'uiViewd4c540907e0a410c9ed5a1e22ab78dbd', // deli.aries.item.DeliItemFloorChildItem 楼层表单
          'uiView5b31876f561e487d97c7d18d8e4eef0b' // deli.aries.trade.DeliAriesSpecialApplyOrderItem 特殊政策申请
        ];
        const reloadDataActionName = [
          'uiViewe58462c8e33648bbb1dc55d18c7ffd13' // deli.seller.livestream.DeliAriesSellerLivestreamItemTplProxy 品牌直播基础商品代理
        ];

        const importDataActionNames = [
          'uiView14ec96b3b091402fabe948b1743cc1ae', // 分销运营工作台-订货会-编辑-商品导入
          'uiView164e1fc1fea742a29704f4cee9b5aed7', // 品牌分销工作台-订货会-编辑-商品导入
          'uiView67dada3338f74e4f9287515ec819a244', // 品牌分销工作台-订货会-编辑-经销商导入
          'uiView4d1ea2d172c24ce59788ae5f1018de3e', // 品牌分销工作台-订货会-添加商品-商品导入
          'uiView0000000000028007', // 经销工作台-订货会管理-我发起的订货会-编辑-商品导入
          'uiView0000000000028008', // 经销工作台-订货会管理-我发起的订货会-编辑-经销商导入
          'uiViewd19e1f35893b45d5920426523df9c4b2', // 分销-直播-创建/更新-商品导入
          'uiView6f61cf9a77b64224bf2014bc64ad0e16', // 品牌-直播-创建/更新-商品导入
          'uiView31179bc65a1f421c9dc1ba69cf67d371', // 分销-订货会-添加商品-商品导入
          'uiView8c0fe00d6550454ea45fbc1705e8ca33',
          'uiViewcc0758b132ff4b0f80f39eb9d7b68649'
        ];
        const unRefreshActionNames = ['uiViewf8dac3c905fc471381881ded3ab50e7e'];
        if (importDataActionNames.includes(this.action?.name)) {
          // 推荐用此方法处理导入，之前的代码太复杂了
          this.addImportDatas(importData, subviewFieldWidget);
        } else if (unRefreshActionNames.includes(this.action?.name)) {
          this.reloadDataSource(importData);
          (subviewFieldWidget as any).formData[subviewFieldWidget.itemName] = importData;
          if (subviewSubmitCache) {
            subviewSubmitCache.pushRecords(importData);
          }
        } else if (deleteActionNames.includes(this.action?.name)) {
          let keys = importData.map((n) => n.outCode);
          this.reloadDataSource(showRecords.filter((n) => !keys.includes(n.outCode)));
          (subviewFieldWidget as any).formData[subviewFieldWidget.itemName] = importData;
          if (subviewSubmitCache) {
            subviewSubmitCache.pushRecords(importData);
          }
        } else {
          this.reloadDataSource(importData);
          subviewFieldWidget.change(importData);

          if (
            ttype === ModelFieldType.OneToMany ||
            (useMatched()?.matched?.segmentParams?.page?.id && ttype === ModelFieldType.ManyToMany) ||
            cacheIncludeModels.includes(this.rootRuntimeContext.model.model)
          ) {
            if (subviewSubmitCache) {
              subviewSubmitCache.pushRecords(importData);
            } else if (submitCache) {
              //@ts-ignore
              if (reloadDataActionName.includes(this?.config?.action?.name)) {
                submitCache.pushRecords(importData); //该行代码会导致删除后 数据错乱的问题
              }
            }
          }
          if (ttype === ModelFieldType.OneToMany || flushDataSourceModels.includes(this.rootRuntimeContext.model.model)) {
            subviewFieldWidget.setValue(this.dataSource);
            //@ts-ignore
            if (!actionNames.includes(this?.action.name)) {
              // subviewFieldWidget.flushDataSource();
            }
          }
        }

        // subviewFieldWidget.afterTriggerExecute?.(WidgetTrigger.CHANGE);
        if (duplicates.length > 0) {
          this.errorArr.push(...duplicates);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  protected addImportDatas(importData: ActiveRecord[], subviewFieldWidget: FormComplexListFieldWidget) {
    this.pushDataSourceForSubmit({ showRecords: importData, submitRecords: importData });

    const tableViewWidget = subviewFieldWidget.getChildrenWidget().find((w) => w.constructor.name == 'TableView');
    if (tableViewWidget) {
      const tableWidget = tableViewWidget.getChildrenWidget().find((w) => w.constructor.name == 'TableWidget');
      tableWidget && ((tableWidget as any).isDataSourceProvider = false);
    }

    subviewFieldWidget?.flushDataSource();
  }

  // 复制于 PopupActionWidget.pushDataSourceForSubmit 方法
  protected pushDataSourceForSubmit(parameters: PopupSubmitParameters, predict?: PushActiveRecordsPredict) {
    const { showRecords, submitRecords } = parameters;
    const subviewSubmitCache = this.metadataRuntimeContext.extendData.subviewSubmitCache as SubmitCacheManager;
    if (subviewSubmitCache) {
      predict = predict || ActiveRecordsOperator.defaultPushPredict.bind(subviewSubmitCache);
      ActiveRecordsOperator.operator(this.dataSource, subviewSubmitCache).push(submitRecords, predict);
    }
    this.pushDataSource(showRecords, predict);
  }

  /**
   * 上传文件
   *
   * @protected
   * @memberof formTableImportV2
   */
  @Widget.Method()
  protected async doImport(e) {
    // 弹出选择文件弹窗
    const input = document.createElement('input');
    input.type = 'file';
    input.style.display = 'none';
    input.onchange = (e) => {
      this.readFile(e);
    };
    input.click();
  }
}
